import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import TransactionCard from './components/TransactionCard';
import TRLSummaryPanel from './components/TRLSummaryPanel';
import FilterControls from './components/FilterControls';
import QuickActions from './components/QuickActions';
import DashboardStats from './components/DashboardStats';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TransactionDashboard = () => {
  const navigate = useNavigate();
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    stage: 'all',
    propertyType: 'all',
    stakeholderRole: 'all',
    deadlineProximity: 'all',
    trlRange: 'all',
    sortBy: 'trl-desc',
    viewMode: 'grid'
  });

  // Mock transaction data
  const mockTransactions = [
    {
      id: "txn-001",
      propertyTitle: "Luxury Downtown Condo",
      location: "Manhattan, New York",
      propertyType: "Residential",
      value: 2500000,
      status: "Active",
      trlScore: 85,
      trlDescription: "Ready for completion - all major documents verified",
      propertyImage: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&h=300&fit=crop",
      isBlockchainVerified: true,
      documentsCompleted: 12,
      documentsTotal: 14,
      signaturesCompleted: 4,
      signaturesRequired: 5,
      daysLeft: 15,
      stakeholders: [
        { role: "Owner", completed: true },
        { role: "Lawyer", completed: true },
        { role: "Engineer", completed: true },
        { role: "Notary", completed: false }
      ],
      nextActions: [
        "Notary signature required for deed transfer",
        "Final property inspection scheduled"
      ],
      lastBlockchainUpdate: "2 hours ago"
    },
    {
      id: "txn-002",
      propertyTitle: "Commercial Office Building",
      location: "Downtown Seattle, WA",
      propertyType: "Commercial",
      value: 8750000,
      status: "Pending",
      trlScore: 65,
      trlDescription: "Documentation phase - engineering reports pending",
      propertyImage: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop",
      isBlockchainVerified: true,
      documentsCompleted: 8,
      documentsTotal: 16,
      signaturesCompleted: 2,
      signaturesRequired: 6,
      daysLeft: 8,
      stakeholders: [
        { role: "Owner", completed: true },
        { role: "Lawyer", completed: false },
        { role: "Engineer", completed: false },
        { role: "Notary", completed: false }
      ],
      nextActions: [
        "Structural engineering report due",
        "Environmental compliance review"
      ],
      lastBlockchainUpdate: "1 day ago"
    },
    {
      id: "txn-003",
      propertyTitle: "Suburban Family Home",
      location: "Austin, Texas",
      propertyType: "Residential",
      value: 650000,
      status: "Review",
      trlScore: 45,
      trlDescription: "Early stage - initial documentation in progress",
      propertyImage: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=400&h=300&fit=crop",
      isBlockchainVerified: false,
      documentsCompleted: 5,
      documentsTotal: 12,
      signaturesCompleted: 1,
      signaturesRequired: 4,
      daysLeft: 25,
      stakeholders: [
        { role: "Owner", completed: true },
        { role: "Lawyer", completed: false },
        { role: "Engineer", completed: false },
        { role: "Notary", completed: false }
      ],
      nextActions: [
        "Property title verification needed",
        "Insurance documentation required"
      ],
      lastBlockchainUpdate: null
    },
    {
      id: "txn-004",
      propertyTitle: "Industrial Warehouse",
      location: "Phoenix, Arizona",
      propertyType: "Industrial",
      value: 3200000,
      status: "Active",
      trlScore: 92,
      trlDescription: "Near completion - final signatures pending",
      propertyImage: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&h=300&fit=crop",
      isBlockchainVerified: true,
      documentsCompleted: 18,
      documentsTotal: 18,
      signaturesCompleted: 5,
      signaturesRequired: 6,
      daysLeft: 3,
      stakeholders: [
        { role: "Owner", completed: true },
        { role: "Lawyer", completed: true },
        { role: "Engineer", completed: true },
        { role: "Notary", completed: false }
      ],
      nextActions: [
        "Final notary signature required",
        "Blockchain timestamp completion"
      ],
      lastBlockchainUpdate: "30 minutes ago"
    },
    {
      id: "txn-005",
      propertyTitle: "Beachfront Villa",
      location: "Miami Beach, Florida",
      propertyType: "Residential",
      value: 4800000,
      status: "Pending",
      trlScore: 38,
      trlDescription: "Documentation issues - legal review required",
      propertyImage: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=400&h=300&fit=crop",
      isBlockchainVerified: false,
      documentsCompleted: 4,
      documentsTotal: 15,
      signaturesCompleted: 0,
      signaturesRequired: 5,
      daysLeft: 45,
      stakeholders: [
        { role: "Owner", completed: false },
        { role: "Lawyer", completed: false },
        { role: "Engineer", completed: false },
        { role: "Notary", completed: false }
      ],
      nextActions: [
        "Resolve title dispute",
        "Environmental impact assessment"
      ],
      lastBlockchainUpdate: null
    },
    {
      id: "txn-006",
      propertyTitle: "Mixed-Use Development",
      location: "Portland, Oregon",
      propertyType: "Mixed Use",
      value: 12500000,
      status: "Active",
      trlScore: 78,
      trlDescription: "Advanced stage - final approvals in progress",
      propertyImage: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&h=300&fit=crop",
      isBlockchainVerified: true,
      documentsCompleted: 22,
      documentsTotal: 28,
      signaturesCompleted: 6,
      signaturesRequired: 8,
      daysLeft: 12,
      stakeholders: [
        { role: "Owner", completed: true },
        { role: "Lawyer", completed: true },
        { role: "Engineer", completed: false },
        { role: "Notary", completed: false }
      ],
      nextActions: [
        "Zoning compliance verification",
        "Final engineering sign-off"
      ],
      lastBlockchainUpdate: "4 hours ago"
    }
  ];

  // Mock dashboard stats
  const dashboardStats = {
    totalTransactions: 23,
    transactionChange: 12,
    averageTRL: 68,
    trlChange: 5,
    activeStakeholders: 45,
    stakeholderChange: 8,
    documentsProcessed: 342,
    documentChange: 15,
    blockchainVerifications: 156,
    blockchainChange: 22,
    completionRate: 78,
    completionChange: 3,
    lastBlockchainUpdate: "30 min ago",
    monthlyDocuments: 89
  };

  // Mock TRL summary data
  const trlSummaryData = {
    averageTRL: 68,
    readyTransactions: 4,
    inProgressTransactions: 12,
    blockedTransactions: 7,
    pendingDocuments: [
      { name: "Environmental Report", transaction: "Commercial Office Building", assignedTo: "Engineer", dueDate: "Today" },
      { name: "Title Verification", transaction: "Suburban Family Home", assignedTo: "Lawyer", dueDate: "Tomorrow" },
      { name: "Insurance Documentation", transaction: "Beachfront Villa", assignedTo: "Owner", dueDate: "Dec 25" },
      { name: "Zoning Compliance", transaction: "Mixed-Use Development", assignedTo: "Lawyer", dueDate: "Dec 28" }
    ],
    upcomingDeadlines: [
      { task: "Final Notary Signature", transaction: "Industrial Warehouse", priority: "High", dueDate: "Dec 24" },
      { task: "Engineering Sign-off", transaction: "Mixed-Use Development", priority: "Medium", dueDate: "Dec 26" },
      { task: "Document Review", transaction: "Commercial Office", priority: "High", dueDate: "Dec 27" }
    ],
    blockchainActivity: [
      { action: "Document Verified", transaction: "Industrial Warehouse", timestamp: "30 min ago", hash: "0x1a2b3c..." },
      { action: "Signature Recorded", transaction: "Luxury Downtown Condo", timestamp: "2 hours ago", hash: "0x4d5e6f..." },
      { action: "TRL Updated", transaction: "Mixed-Use Development", timestamp: "4 hours ago", hash: "0x7g8h9i..." }
    ],
    avgCompletionTime: "28 days",
    stakeholderEfficiency: 87
  };

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    let filtered = [...mockTransactions];

    // Apply search filter
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter(transaction =>
        transaction?.propertyTitle?.toLowerCase()?.includes(searchTerm) ||
        transaction?.location?.toLowerCase()?.includes(searchTerm) ||
        transaction?.id?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Apply stage filter
    if (filters?.stage !== 'all') {
      filtered = filtered?.filter(transaction => {
        switch (filters?.stage) {
          case 'initiated': return transaction?.trlScore < 30;
          case 'documentation': return transaction?.trlScore >= 30 && transaction?.trlScore < 60;
          case 'review': return transaction?.status === 'Review';
          case 'signatures': return transaction?.trlScore >= 60 && transaction?.trlScore < 80;
          case 'completion': return transaction?.trlScore >= 80 && transaction?.trlScore < 100;
          case 'completed': return transaction?.trlScore === 100;
          default: return true;
        }
      });
    }

    // Apply property type filter
    if (filters?.propertyType !== 'all') {
      filtered = filtered?.filter(transaction => 
        transaction?.propertyType?.toLowerCase()?.replace(' ', '-') === filters?.propertyType
      );
    }

    // Apply TRL range filter
    if (filters?.trlRange !== 'all') {
      filtered = filtered?.filter(transaction => {
        const score = transaction?.trlScore;
        switch (filters?.trlRange) {
          case '80-100': return score >= 80;
          case '60-79': return score >= 60 && score < 80;
          case '40-59': return score >= 40 && score < 60;
          case '0-39': return score < 40;
          default: return true;
        }
      });
    }

    // Apply deadline proximity filter
    if (filters?.deadlineProximity !== 'all') {
      filtered = filtered?.filter(transaction => {
        const days = transaction?.daysLeft;
        switch (filters?.deadlineProximity) {
          case 'overdue': return days < 0;
          case 'today': return days === 0;
          case 'week': return days > 0 && days <= 7;
          case 'month': return days > 7 && days <= 30;
          case 'future': return days > 30;
          default: return true;
        }
      });
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      switch (filters?.sortBy) {
        case 'trl-desc': return b?.trlScore - a?.trlScore;
        case 'trl-asc': return a?.trlScore - b?.trlScore;
        case 'deadline-asc': return a?.daysLeft - b?.daysLeft;
        case 'deadline-desc': return b?.daysLeft - a?.daysLeft;
        case 'value-desc': return b?.value - a?.value;
        case 'value-asc': return a?.value - b?.value;
        default: return 0;
      }
    });

    return filtered;
  }, [filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      stage: 'all',
      propertyType: 'all',
      stakeholderRole: 'all',
      deadlineProximity: 'all',
      trlRange: 'all',
      sortBy: 'trl-desc',
      viewMode: 'grid'
    });
  };

  const handleBulkAction = (actionId, transactionIds) => {
    console.log('Bulk action:', actionId, 'for transactions:', transactionIds);
    // Implement bulk action logic
    setSelectedTransactions([]);
  };

  const handleTransactionSelect = (transactionId, isSelected) => {
    if (isSelected) {
      setSelectedTransactions(prev => [...prev, transactionId]);
    } else {
      setSelectedTransactions(prev => prev?.filter(id => id !== transactionId));
    }
  };

  const handleSelectAll = () => {
    if (selectedTransactions?.length === filteredTransactions?.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(filteredTransactions?.map(t => t?.id));
    }
  };

  // Real-time updates simulation
  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate real-time TRL score updates
      console.log('Real-time update check...');
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumbs />
      <main className="pt-32 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Transaction Dashboard
                </h1>
                <p className="text-muted-foreground">
                  Monitor real estate transaction progress through the Transaction Readiness Level (TRL) system
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => window.location?.reload()}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Refresh
                </Button>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={16} />
                  <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Stats */}
          <DashboardStats stats={dashboardStats} />

          {/* Quick Actions */}
          <QuickActions
            selectedTransactions={selectedTransactions}
            onBulkAction={handleBulkAction}
          />

          {/* Filter Controls */}
          <FilterControls
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            resultCount={filteredTransactions?.length}
          />

          {/* Main Content Area */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* Transaction Cards */}
            <div className="xl:col-span-3">
              {/* Selection Controls */}
              {filteredTransactions?.length > 0 && (
                <div className="flex items-center justify-between mb-6 p-4 bg-card border border-border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTransactions?.length === filteredTransactions?.length}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-accent"
                      />
                      <span className="text-sm text-foreground">
                        Select All ({filteredTransactions?.length})
                      </span>
                    </label>
                    {selectedTransactions?.length > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {selectedTransactions?.length} selected
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">
                      View: {filters?.viewMode === 'grid' ? 'Grid' : 'List'}
                    </span>
                  </div>
                </div>
              )}

              {/* Transaction Grid/List */}
              {filteredTransactions?.length > 0 ? (
                <div className={`grid gap-6 ${
                  filters?.viewMode === 'grid' ?'grid-cols-1 lg:grid-cols-2' :'grid-cols-1'
                }`}>
                  {filteredTransactions?.map((transaction) => (
                    <div key={transaction?.id} className="relative">
                      {/* Selection Checkbox */}
                      <div className="absolute top-4 left-4 z-10">
                        <input
                          type="checkbox"
                          checked={selectedTransactions?.includes(transaction?.id)}
                          onChange={(e) => handleTransactionSelect(transaction?.id, e?.target?.checked)}
                          className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-accent bg-white"
                        />
                      </div>
                      <TransactionCard transaction={transaction} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-card border border-border rounded-lg">
                  <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No transactions found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your filters or search criteria
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    iconName="X"
                    iconPosition="left"
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>

            {/* TRL Summary Panel */}
            <div className="xl:col-span-1">
              <div className="sticky top-32">
                <TRLSummaryPanel summaryData={trlSummaryData} />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TransactionDashboard;